import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";
import {
  TextField,
  Typography,
  Button,
  Snackbar,
  Collapse,
  Link,
} from "@mui/material";

import { API_get_bookings_id, API_edit_price_sheet } from "../utils/http";
import { computeSheet, CELL_ERROR, formatValue } from "../utils/formula";
import {
  parseClipboardGrid,
  isMultiCell,
  applyPaste,
  cellRange,
  rangeBounds,
  buildTSV,
  COLS,
  MAX_ROW,
} from "../utils/paste";

const ROWS = Array.from({ length: MAX_ROW }, (_, i) => i + 1);
// Narrow cells: the Calc tab only occupies ~half the screen.
const CELL_W = 76;

// Canonical form of the grid for dirty-checking: only non-empty raw strings,
// key-sorted. Ignores the `value` field the server sends and whitespace-only
// cells, so a load then a revert-to-original both read as "not dirty".
function serialize(cells) {
  const out = {};
  Object.keys(cells)
    .sort()
    .forEach((k) => {
      const raw = cells[k]?.raw ?? "";
      if (raw.trim() !== "") out[k] = raw;
    });
  return JSON.stringify(out);
}

export default function InputLeftPriceSheet({ booking_id, active }) {
  // Sparse map of coordinate -> { raw }. Only non-empty cells are kept.
  const [cells, set_cells] = useState({});
  const [focused, set_focused] = useState(null);
  const [saved_open, set_saved_open] = useState(false);
  const [toast, set_toast] = useState("");
  // Serialized cells as last loaded/saved, to detect unsaved edits.
  const [saved_json, set_saved_json] = useState("{}");
  const [show_help, set_show_help] = useState(false);
  // Excel-style "point mode": while editing a formula, drag over cells to
  // insert a reference. `sel` drives the highlight; the refs hold live state
  // for the window-level mouseup so handlers never read stale values.
  const [sel, set_sel] = useState(null); // { anchor, current } during a drag
  const input_refs = useRef({}); // cell key -> input DOM node
  const drag_ref = useRef(null); // { formulaKey, anchor, current, caretStart, caretEnd }
  const pending_caret = useRef(null); // { key, pos } to apply after re-render
  // Normal-mode range selection (drag while not editing a formula): highlight a
  // block of cells so Delete can clear them at once.
  const [nsel, set_nsel] = useState(null); // { anchor, current }
  const ndrag_ref = useRef(null); // { anchor } while dragging a normal selection

  // Fetch only when the tab is first opened (the component stays mounted for
  // the whole booking, so this runs once per booking rather than on every
  // InputLeft mount). State is then preserved across tab switches.
  const loaded_booking = useRef(null);
  useEffect(() => {
    if (!active || !booking_id) return;
    if (loaded_booking.current === booking_id) return;
    loaded_booking.current = booking_id;
    API_get_bookings_id(booking_id, (x) => {
      if (x.success) {
        const loaded = x.data?.price_sheet?.cells || {};
        set_cells(loaded);
        set_saved_json(serialize(loaded));
      }
    });
  }, [active, booking_id]);

  // Unsaved edits exist when the current grid differs from the last saved copy.
  const dirty = serialize(cells) !== saved_json;

  // Warn before leaving (reload/close) while there are unsaved edits. In-app
  // tab switches are safe because the component stays mounted.
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // Recompute every value from the raw inputs (client-side, <=80 cells).
  const values = useMemo(() => computeSheet(cells), [cells]);

  const set_cell_raw = (key, raw) => {
    set_cells((prev) => {
      const next = { ...prev };
      if (raw === "") delete next[key];
      else next[key] = { raw };
      return next;
    });
  };

  // Paste a copied Excel/Sheets region starting at `key`. Single-cell pastes
  // fall through to the default TextField behaviour.
  const handlePaste = (key, e) => {
    const text = e.clipboardData.getData("text");
    if (!isMultiCell(text)) return;
    e.preventDefault();
    const grid = parseClipboardGrid(text);
    const { cells: next, clipped } = applyPaste(cells, key, grid);
    set_cells(next);
    set_toast(
      clipped
        ? `Đã dán. Lưới tối đa ${COLS.length}×${MAX_ROW} nên ${clipped} ô ngoài lưới đã bị bỏ.`
        : "Đã dán"
    );
  };

  const handleSave = () => {
    // Persist raw + computed value for each non-empty cell.
    const payload_cells = {};
    Object.keys(cells).forEach((key) => {
      const raw = cells[key]?.raw ?? "";
      if (raw.trim() === "") return;
      payload_cells[key] = { raw, value: values[key] };
    });

    API_edit_price_sheet(booking_id, { cells: payload_cells }, (x) => {
      if (x.success) {
        set_saved_open(true);
        set_saved_json(serialize(cells));
      }
    });
  };

  // Empty the grid locally. Not persisted until the user hits Save, so a
  // reload still restores the server copy if this was a mistake.
  const handleClear = () => {
    set_cells({});
    set_toast("Đã xoá — bấm Save để lưu");
  };

  // The cell currently being edited as a formula (raw begins with "="), or null.
  const formula_key =
    focused && (cells[focused]?.raw ?? "").startsWith("=") ? focused : null;

  // Finish a point-mode drag: insert the picked reference/range into the
  // formula at the caret captured when the drag began. Stable identity so the
  // matching window listener can be removed.
  const finish_drag = useCallback(() => {
    window.removeEventListener("mouseup", finish_drag);
    const d = drag_ref.current;
    drag_ref.current = null;
    set_sel(null);
    if (!d) return;

    const ref_text = cellRange(d.anchor, d.current);
    set_cells((prev) => {
      const raw = prev[d.formulaKey]?.raw ?? "";
      const next = raw.slice(0, d.caretStart) + ref_text + raw.slice(d.caretEnd);
      const map = { ...prev };
      if (next === "") delete map[d.formulaKey];
      else map[d.formulaKey] = { raw: next };
      return map;
    });
    pending_caret.current = {
      key: d.formulaKey,
      pos: d.caretStart + ref_text.length,
    };
  }, []);

  // Start a drag when clicking a cell other than the one whose formula is being
  // edited. Returning false lets the target cell focus normally instead.
  const start_pick = (targetKey) => {
    if (!formula_key || targetKey === formula_key) return false;
    const el = input_refs.current[formula_key];
    const caretStart = el ? el.selectionStart : (cells[formula_key]?.raw ?? "").length;
    const caretEnd = el ? el.selectionEnd : caretStart;
    drag_ref.current = { formulaKey: formula_key, anchor: targetKey, current: targetKey, caretStart, caretEnd };
    set_sel({ anchor: targetKey, current: targetKey });
    window.addEventListener("mouseup", finish_drag);
    return true;
  };

  const extend_pick = (targetKey) => {
    if (!drag_ref.current) return;
    drag_ref.current.current = targetKey;
    set_sel({ anchor: drag_ref.current.anchor, current: targetKey });
  };

  // End of a normal-mode drag: the highlighted selection stays put for Delete.
  const end_normal_drag = useCallback(() => {
    window.removeEventListener("mouseup", end_normal_drag);
    ndrag_ref.current = null;
  }, []);

  // Mouse handlers shared by every cell. Point-mode (formula editing) takes
  // priority; otherwise a drag builds a normal-mode selection.
  const cell_mouse_down = (key, e) => {
    if (start_pick(key)) {
      e.preventDefault(); // keep focus on the formula cell
      return;
    }
    ndrag_ref.current = { anchor: key };
    window.addEventListener("mouseup", end_normal_drag);
    // A plain click focuses the cell for editing (see onFocus clearing nsel);
    // only dragging onto another cell turns it into a selection.
  };

  const cell_mouse_enter = (key) => {
    if (drag_ref.current) {
      extend_pick(key);
      return;
    }
    if (ndrag_ref.current) {
      set_nsel({ anchor: ndrag_ref.current.anchor, current: key });
      // Stop editing so Delete/Escape act on the whole selection.
      if (document.activeElement && document.activeElement.blur) {
        document.activeElement.blur();
      }
    }
  };

  // Drop the selection when the tab is hidden so a stale range can't linger.
  useEffect(() => {
    if (!active) set_nsel(null);
  }, [active]);

  // While a normal-mode selection exists (no cell is being edited), Delete /
  // Backspace clears every cell in it and Escape dismisses the selection.
  // Gated on `active`: the component stays mounted across tab switches, so the
  // window listener must not fire while another tab is showing.
  useEffect(() => {
    if (!active || !nsel) return undefined;
    const onKey = (e) => {
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        const b = rangeBounds(nsel.anchor, nsel.current);
        if (!b) return;
        set_cells((prev) => {
          const map = { ...prev };
          for (let c = b.colLo; c <= b.colHi; c++) {
            for (let r = b.rowLo; r <= b.rowHi; r++) delete map[COLS[c] + r];
          }
          return map;
        });
      } else if (e.key === "Escape") {
        set_nsel(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, nsel]);

  // Ctrl/Cmd+C over a normal-mode selection copies the block as TSV so it can
  // be pasted into Excel. (While editing a cell, the browser's default copy of
  // the cell text is left untouched — nsel is null then.)
  useEffect(() => {
    if (!active || !nsel) return undefined;
    const onCopy = (e) => {
      const b = rangeBounds(nsel.anchor, nsel.current);
      if (!b) return;
      e.preventDefault();
      e.clipboardData.setData("text/plain", buildTSV(values, b));
      set_toast("Đã copy");
    };
    window.addEventListener("copy", onCopy);
    return () => window.removeEventListener("copy", onCopy);
  }, [active, nsel, values]);

  // After a pick re-renders the formula, restore focus + caret past the ref.
  useLayoutEffect(() => {
    const p = pending_caret.current;
    if (!p) return;
    pending_caret.current = null;
    const el = input_refs.current[p.key];
    if (el) {
      el.focus();
      el.setSelectionRange(p.pos, p.pos);
    }
  });

  // Highlight bounds for whichever selection is active (formula pick or normal).
  const active_sel = sel || nsel;
  const sel_bounds = active_sel
    ? rangeBounds(active_sel.anchor, active_sel.current)
    : null;
  const in_selection = (colIdx, row) =>
    sel_bounds &&
    colIdx >= sel_bounds.colLo &&
    colIdx <= sel_bounds.colHi &&
    row >= sel_bounds.rowLo &&
    row <= sel_bounds.rowHi;

  // Move focus to another cell by column letter + row number (clamped to grid).
  const focus_cell = (col, row) => {
    if (row < 1 || row > MAX_ROW) return;
    const el = input_refs.current[col + row];
    if (el) {
      el.focus();
      el.select();
    }
  };

  // Move focus by a column offset from the given cell (clamped to A-G).
  const focus_col = (col, row, delta) => {
    const idx = COLS.indexOf(col) + delta;
    if (idx < 0 || idx >= COLS.length) return;
    focus_cell(COLS[idx], row);
  };

  // Tab flows right and wraps to the next row's first column; Shift+Tab flows
  // left and wraps to the previous row's last column.
  const focus_tab = (col, row, back) => {
    let idx = COLS.indexOf(col) + (back ? -1 : 1);
    let nextRow = row;
    if (idx >= COLS.length) {
      idx = 0;
      nextRow = row + 1;
    } else if (idx < 0) {
      idx = COLS.length - 1;
      nextRow = row - 1;
    }
    focus_cell(COLS[idx], nextRow);
  };

  // Enter / ArrowDown commit the current cell and drop to the row below;
  // ArrowUp goes up. Left/Right jump columns only when the caret is already at
  // the edge of the text, so they still move the caret while editing mid-string.
  // Tab / Shift+Tab move across columns and wrap between rows.
  const handle_key = (col, row, e) => {
    const el = e.target;
    const collapsed = el.selectionStart === el.selectionEnd;
    if (e.key === "Tab") {
      e.preventDefault();
      focus_tab(col, row, e.shiftKey);
    } else if (e.key === "Enter" || e.key === "ArrowDown") {
      e.preventDefault();
      focus_cell(col, row + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      focus_cell(col, row - 1);
    } else if (e.key === "ArrowRight" && collapsed && el.selectionStart === el.value.length) {
      e.preventDefault();
      focus_col(col, row, 1);
    } else if (e.key === "ArrowLeft" && collapsed && el.selectionStart === 0) {
      e.preventDefault();
      focus_col(col, row, -1);
    }
  };

  // When focused, show the editable raw. Otherwise show the computed value
  // with Vietnamese-style thousand grouping.
  const display_value = (key) => {
    if (focused === key) return cells[key]?.raw ?? "";
    const value = values[key];
    return value === undefined ? "" : formatValue(value);
  };

  return (
    <div>
      <Typography variant="h4">Calc</Typography>

      <div
        style={{
          position: "absolute",
          right: 10,
          top: 10,
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        {dirty ? (
          <Typography style={{ fontSize: 12, color: "#ed6c02" }}>
            ● Chưa lưu
          </Typography>
        ) : null}
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={handleClear}
        >
          Clear data
        </Button>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </div>

      <Typography variant="body2" style={{ color: "#666", marginBottom: 6 }}>
        Bảng dùng để <b>dán dữ liệu từ Excel</b> vào, chỉnh sửa lại, rồi bấm{" "}
        <b>Save</b>.{" "}
        <Link
          component="button"
          type="button"
          onClick={() => set_show_help((v) => !v)}
          sx={{ fontSize: "inherit", verticalAlign: "baseline" }}
        >
          {show_help ? "Thu gọn hướng dẫn" : "Xem hướng dẫn"}
        </Link>
      </Typography>

      <Collapse in={show_help}>
        <Typography
          variant="body2"
          component="div"
          style={{
            color: "#555",
            marginBottom: 10,
            padding: "10px 14px",
            background: "#F9FAFB",
            borderRadius: 8,
            border: "1px solid #EEF0F2",
          }}
        >
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
            <li>
              <b>Dán từ Excel:</b> chọn ô làm gốc rồi <b>Ctrl+V</b> — dữ liệu
              trải sang phải và xuống dưới. Lưới tối đa{" "}
              <b>{COLS.length} cột × {MAX_ROW} dòng</b>, phần vượt ra sẽ bị bỏ.
            </li>
            <li>
              <b>Copy sang Excel:</b> kéo chuột bôi một vùng, rồi <b>Ctrl+C</b> —
              dán thẳng vào Excel.
            </li>
            <li>
              <b>Di chuyển ô:</b> <b>Enter</b> hoặc <b>↓</b> xuống, <b>↑</b> lên,{" "}
              <b>Tab / Shift+Tab</b> sang phải/trái, <b>← →</b> khi con trỏ đã ở
              đầu/cuối chữ trong ô.
            </li>
            <li>
              <b>Xoá nhanh:</b> kéo bôi vùng rồi bấm <b>Delete</b> (nhấn{" "}
              <b>Esc</b> để bỏ chọn). Nút <b>Clear data</b> xoá toàn bộ.
            </li>
            <li>
              <b>Công thức:</b> gõ bắt đầu bằng <b>=</b> (vd{" "}
              <code>=A1+B2*3</code>, <code>=SUM(A1:A5)</code>). Đang gõ công thức
              thì click hoặc kéo ô khác để tự chèn tham chiếu.
            </li>
            <li>
              <b>Lưu:</b> mọi thay đổi chỉ được lưu khi bấm <b>Save</b>; khi có
              thay đổi chưa lưu sẽ hiện dấu <b style={{ color: "#ed6c02" }}>●
              Chưa lưu</b>.
            </li>
          </ul>
        </Typography>
      </Collapse>

      <table style={{ borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={headerCornerStyle} />
            {COLS.map((col) => (
              <th key={col} style={headerCellStyle}>
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ROWS.map((row) => (
            <tr key={row}>
              <td style={headerRowStyle}>{row}</td>
              {COLS.map((col, colIdx) => {
                const key = col + row;
                const is_error = values[key] === CELL_ERROR;
                const selected = in_selection(colIdx, row);
                const picking = formula_key && key !== formula_key;
                return (
                  <td
                    key={key}
                    style={{
                      ...cellStyle,
                      background: selected ? "#E3F2FD" : undefined,
                      cursor: picking ? "cell" : undefined,
                    }}
                    onMouseDown={(e) => cell_mouse_down(key, e)}
                    onMouseEnter={() => cell_mouse_enter(key)}
                  >
                    <TextField
                      value={display_value(key)}
                      inputRef={(el) => {
                        if (el) input_refs.current[key] = el;
                        else delete input_refs.current[key];
                      }}
                      onFocus={() => {
                        set_focused(key);
                        set_nsel(null); // editing clears any range selection
                      }}
                      onBlur={() => set_focused(null)}
                      onChange={(e) => set_cell_raw(key, e.target.value)}
                      onKeyDown={(e) => handle_key(col, row, e)}
                      onPaste={(e) => handlePaste(key, e)}
                      variant="standard"
                      InputProps={{ disableUnderline: true }}
                      inputProps={{
                        style: {
                          padding: "3px 5px",
                          fontSize: 13,
                          color: is_error ? "#d32f2f" : undefined,
                          cursor: picking ? "cell" : undefined,
                        },
                      }}
                      style={{ width: CELL_W }}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <Snackbar
        open={saved_open}
        autoHideDuration={2000}
        onClose={() => set_saved_open(false)}
        message="Đã lưu"
      />

      <Snackbar
        open={toast !== ""}
        autoHideDuration={2500}
        onClose={() => set_toast("")}
        message={toast}
      />
    </div>
  );
}

const headerCellStyle = {
  border: "1px solid #ddd",
  background: "#f5f5f5",
  width: CELL_W,
  padding: "3px 5px",
  fontSize: 12,
  fontWeight: 600,
  textAlign: "center",
};

const headerCornerStyle = {
  border: "1px solid #ddd",
  background: "#f5f5f5",
  width: 28,
};

const headerRowStyle = {
  border: "1px solid #ddd",
  background: "#f5f5f5",
  textAlign: "center",
  fontWeight: 600,
  fontSize: 12,
  padding: "3px 5px",
};

const cellStyle = {
  border: "1px solid #ddd",
  padding: 0,
};
